import { AppCard, LoadingDots } from "../../components/ui";
import { Component } from "react";
import { toastService } from "../../services/toastService";
import { InputSwitch } from "primereact/inputswitch";
import { Merchant } from "../../models/MerchantModel";
import { merchantApiService } from "../../services/api/merchants/MerchantApiService";
import { fileService } from "../../services/FileService";
import { InputNumber } from "primereact/inputnumber";
import { utils } from "../../utils/utils";
import { Shield, ShieldOff, ShieldCheck, CreditCard } from "lucide-react";

interface ScaSectionsProps {
    merchantId: number | undefined;
    merchant: Merchant;
}

interface ScaSectionsState {
    merchantData?: Merchant | null;
    isLoading: boolean;
    [key: string]: any;
}

interface ScaFeature {
    label: string;
    enabledKey: keyof Merchant;
    daysKey: keyof Merchant;
    description: string;
}

const SCA_FEATURES: ScaFeature[] = [
    { label: "Login", enabledKey: "scaEnabledForLogin", daysKey: "scaEnabledDaysForLogin", description: "Require SCA verification on user login" },
    { label: "Login PIN", enabledKey: "scaEnabledForLoginPin", daysKey: "scaEnabledDaysForLoginPin", description: "Require SCA when a customer sets or changes the wallet login PIN" },
    { label: "Recurring Login", enabledKey: "scaEnabledForRecurringLogin", daysKey: "scaEnabledDaysForRecurringLogin", description: "Require SCA for recurring login sessions" },
    { label: "PSD Login", enabledKey: "scaEnabledForPsdLogin", daysKey: "scaEnabledDaysPsdLogin", description: "PSD2 compliant login authentication" },
    { label: "PSD Transactions", enabledKey: "scaEnabledForPsdTransaction", daysKey: "scaEnabledDaysForPsdTransaction", description: "PSD2 compliant transaction authorization" },
    { label: "Mobile Number", enabledKey: "scaEnabledForMobileNumber", daysKey: "scaEnabledDaysForMobileNumber", description: "Verify mobile number changes via SCA" },
    { label: "Payment Operations", enabledKey: "scaEnabledForPaymentOperation", daysKey: "scaEnabledDaysForPaymentOperation", description: "Require SCA for payment operations" },
    { label: "Password", enabledKey: "scaEnabledForPassword", daysKey: "scaEnabledDaysForPassword", description: "Require SCA on password changes" },
    { label: "Template Payment", enabledKey: "scaEnabledForTemplatePayment", daysKey: "scaEnabledDaysForTemplatePayment", description: "Require SCA for template-based payments" },
];

export default class ScaSectionsComponent extends Component<ScaSectionsProps, ScaSectionsState> {
    constructor(props: ScaSectionsProps | Readonly<ScaSectionsProps>) {
        super(props);
        this.state = {
            merchantData: null,
            isLoading: true,
        };
    }

    async componentDidMount() {
        await this.loadMerchant();
    }

    loadMerchant = async () => {
        if (this.props.merchantId == undefined || this.props.merchantId == 0) {
            return;
        }

        const merchantResult = await merchantApiService.get(this.props.merchantId!);
        if (!merchantResult.success) {
            this.setState({ isLoading: false });
            toastService.error(merchantResult.message);
            return;
        }

        this.setState({ merchantData: merchantResult.data });
        this.setState({
            isLoading: false,
            merchantData: merchantResult.data,
        });
        this.loadImage();
    };
    loadImage = async () => {
        if (!this.state.merchantData || !this.state.merchantData.logo || this.state.merchantData.logo.length === 0) {
            return;
        }

        const logoResult = await fileService.GetFile(this.state.merchantData.logo);

        if (!logoResult.success) {
            toastService.error("Could not load Merchant Logo. Please contact your administrator");
            return;
        }

        const merchant = this.state.merchantData;
        merchant.logo = logoResult.data;

        this.setState({
            merchantData: merchant,
        });
    };

    handleSwitchChange = async (stateKey: keyof Merchant) => {
        const { merchantData } = this.state;

        if (!merchantData) {
            return;
        }
        this.setState((prevState) => ({ ...prevState, [stateKey]: !prevState[stateKey] }));

        let updatedMerchant: Merchant;
        if (stateKey === "scaEnabled") {
            updatedMerchant = {
                ...merchantData,
                scaEnabled: !merchantData.scaEnabled,
                scaEnabledForLogin: false,
                scaEnabledForRecurringLogin: false,
                scaEnabledForMobileNumber: false,
                scaEnabledForPaymentOperation: false,
                scaEnabledForPassword: false,
                scaEnabledForTemplatePayment: false,
                scaEnabledForPsdLogin: false,
                scaEnabledForPsdTransaction: false,
                scaEnabledForLoginPin: false,
            };
        } else if (stateKey === "scaEnabledForRecurringLogin") {
            updatedMerchant = {
                ...merchantData,
                scaEnabledForRecurringLogin: !merchantData.scaEnabledForRecurringLogin,
                scaEnabledForLogin: false,
            };
        } else if (stateKey === "scaEnabledForLogin") {
            updatedMerchant = {
                ...merchantData,
                scaEnabledForLogin: !merchantData.scaEnabledForLogin,
                scaEnabledForRecurringLogin: false,
            };
        } else {
            updatedMerchant = {
                ...merchantData,
                [stateKey]: !merchantData[stateKey],
            };
        }

        try {
            this.setState({ isLoading: true });

            const lookupResult = await merchantApiService.update(updatedMerchant);
            if (!lookupResult.success) {
                this.setState({ isLoading: false });
                toastService.error(lookupResult.message);
                return;
            } else {
                this.setState({
                    isLoading: false,
                    merchantData: updatedMerchant,
                });
                toastService.success("SCA Updated Sucessfully");
            }
        } catch (error) {
            this.setState({
                isLoading: false,
            });
            console.error("Error updating merchant:", error);
            toastService.error("Failed to update merchant");

            this.setState((prevState) => ({ ...prevState, [stateKey]: !prevState[stateKey] }));
        }
    };
    handleInputNumberChange = async (value: number, stateKey: keyof Merchant) => {
        const { merchantData } = this.state;

        if (!merchantData) {
            return;
        }

        // Construct the updated merchant object
        const updatedMerchant: Merchant = { ...merchantData };
        let saveDateKey: keyof Merchant;

        switch (stateKey) {
            case "scaEnabledDaysForLogin":
                updatedMerchant.scaEnabledDaysForLogin = value;
                saveDateKey = "lastDateForLogin";
                break;
            case "scaEnabledDaysForRecurringLogin":
                updatedMerchant.scaEnabledDaysForRecurringLogin = value;
                saveDateKey = "lastDateForRecurringLogin";
                break;
            case "scaEnabledDaysForMobileNumber":
                updatedMerchant.scaEnabledDaysForMobileNumber = value;
                saveDateKey = "lastDateForMobileNumber";
                break;
            case "scaEnabledDaysForPaymentOperation":
                updatedMerchant.scaEnabledDaysForPaymentOperation = value;
                saveDateKey = "lastDateForPaymentOperation";
                break;
            case "scaEnabledDaysForPassword":
                updatedMerchant.scaEnabledDaysForPassword = value;
                saveDateKey = "lastDateForPassword";
                break;
            case "scaEnabledDaysForTemplatePayment":
                updatedMerchant.scaEnabledDaysForTemplatePayment = value;
                saveDateKey = "lastDateForTemplatePayment";
                break;
            case "scaEnabledDaysPsdLogin":
                updatedMerchant.scaEnabledDaysPsdLogin = value;
                saveDateKey = "lastDateForPsdLogin";
                break;
            case "scaEnabledDaysForPsdTransaction":
                updatedMerchant.scaEnabledDaysForPsdTransaction = value;
                saveDateKey = "lastDateForPsdTransaction";
                break;
            case "scaEnabledDaysForLoginPin":
                updatedMerchant.scaEnabledDaysForLoginPin = value;
                saveDateKey = "lastDateForLoginPin";
                break;
            default:
                // No match, return without updating
                return;
        }

        // Set SaveDate property to current date and time
        updatedMerchant[saveDateKey] = utils.convertDateToUTC(new Date());

        try {
            this.setState({ isLoading: true });
            const lookupResult = await merchantApiService.update(updatedMerchant);
            if (!lookupResult.success) {
                this.setState({ isLoading: false });
                toastService.error(lookupResult.message);
                return;
            } else {
                this.setState({
                    isLoading: false,
                    merchantData: updatedMerchant,
                });
                toastService.success("SCA Updated Sucessfully");
            }
        } catch (error) {
            this.setState({
                isLoading: false,
            });
            console.error("Error updating merchant:", error);
            toastService.error("Failed to update merchant");

            this.setState((prevState) => ({ ...prevState, [stateKey]: !prevState[stateKey] }));
        }
    };

    renderFeatureRow = (feature: ScaFeature) => {
        const merchant = this.state.merchantData;
        if (!merchant) return null;

        const scaEnabled = merchant.scaEnabled ?? false;
        const featureEnabled = (merchant[feature.enabledKey] as boolean) ?? false;
        const daysValue = (merchant[feature.daysKey] as number) ?? 0;

        return (
            <div
                key={feature.enabledKey}
                className={`flex items-center justify-between py-3 px-4 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0 transition-opacity ${!scaEnabled ? "opacity-40 pointer-events-none" : ""}`}
            >
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{feature.label}</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{feature.description}</div>
                </div>

                <div className="flex items-center gap-4 ml-4">
                    <div className={`flex items-center gap-2 transition-opacity ${!featureEnabled ? "opacity-40" : ""}`}>
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 whitespace-nowrap">Every</span>
                        <InputNumber
                            tooltip="Number of days between SCA checks. 0 = daily."
                            tooltipOptions={{ position: "top" }}
                            disabled={!scaEnabled || !featureEnabled}
                            value={daysValue}
                            onValueChange={(e) => this.handleInputNumberChange(e.value ?? 0, feature.daysKey)}
                            mode="decimal"
                            min={0}
                            max={360}
                            className="w-16"
                            inputClassName="!py-1.5 !px-2 !text-xs text-center"
                        />
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">days</span>
                    </div>
                    <InputSwitch
                        disabled={!scaEnabled}
                        checked={featureEnabled}
                        onChange={() => this.handleSwitchChange(feature.enabledKey)}
                    />
                </div>
            </div>
        );
    };

    render() {
        if (this.state.isLoading && !this.state.merchantData) {
            return (
                <AppCard><AppCard.Header title="Strong Customer Authorization" /><AppCard.Body>
                    <LoadingDots />
                </AppCard.Body></AppCard>
            );
        }

        const scaEnabled = this.state.merchantData?.scaEnabled ?? false;

        const inAppScaEnabled = this.state.merchantData?.inAppScaEnabled ?? false;

        return (
            <AppCard>
                <AppCard.Header title="Strong Customer Authorization" />
                <AppCard.Body className="!p-0">
                    {/* SCA Provider Toggle */}
                    <div className="flex items-center justify-between px-4 py-4 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50">
                        <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${inAppScaEnabled ? "bg-success-100 text-success-600 dark:bg-success-900/30 dark:text-success-400" : "bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400"}`}>
                                {inAppScaEnabled ? <ShieldCheck size={18} /> : <CreditCard size={18} />}
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                                    In-App SCA {inAppScaEnabled ? "Enabled" : "Disabled"}
                                </div>
                                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                    Use In-App SCA flow instead of SealApp flow
                                </div>
                            </div>
                        </div>
                        <InputSwitch
                            checked={inAppScaEnabled}
                            onChange={() => this.handleSwitchChange("inAppScaEnabled")}
                        />
                    </div>

                    {/* Master SCA Toggle */}
                    <div className={`flex items-center justify-between px-4 py-4 border-b border-neutral-200 dark:border-neutral-700 ${scaEnabled ? "bg-success-50/50 dark:bg-success-900/10" : ""}`}>
                        <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${scaEnabled ? "bg-success-100 text-success-600 dark:bg-success-900/30 dark:text-success-400" : "bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500"}`}>
                                {scaEnabled ? <Shield size={18} /> : <ShieldOff size={18} />}
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                                    SCA {scaEnabled ? "Enabled" : "Disabled"}
                                </div>
                                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                    {scaEnabled ? "Strong Customer Authentication is active for this merchant" : "Enable to configure individual SCA policies below"}
                                </div>
                            </div>
                        </div>
                        <InputSwitch
                            checked={scaEnabled}
                            onChange={() => this.handleSwitchChange("scaEnabled")}
                        />
                    </div>

                    {/* Feature Toggles */}
                    <div className="relative">
                        {this.state.isLoading && (
                            <div className="absolute inset-0 bg-white/60 dark:bg-neutral-900/60 z-10 flex items-center justify-center">
                                <LoadingDots />
                            </div>
                        )}
                        {SCA_FEATURES.map((feature) => this.renderFeatureRow(feature))}
                    </div>
                </AppCard.Body>
            </AppCard>
        );
    }
}
